import { useAtomValue } from 'jotai';
import { startOfDay } from 'date-fns';
import { useMemo } from 'react';

import { transactionsToCashflows } from '@foresightdata/metrics-calculator';
import { LineChartData, TQuoter } from '../../../types';
import { calculatePoolIrr } from '../providers/calculatePoolIrr';
import { QuartersBuilder } from '../services/QuartersBuilder';
import { mapLineChartData } from '../../../components/Charts/LineChart/mapLineChartData';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { selectedViewDatePFJ } from '../state/PageStateJ';
import { IMetricsDataModel } from '../../../data-models/metrics.data-model';
import { useCompanyMapLookup } from '../../../services/hooks/useSaveCompanyData';
import { MetricsTransactionDataModel } from '../../../schemas/MetricsTransaction.schema';

type TQuarterTransactions = Record<string, MetricsTransactionDataModel[]>;

export type GroupExtractorFn = (params: {
  transaction: MetricsTransactionDataModel;
  company: ICompanyDataModel;
}) => string;

interface IrrByGroup {
  data: LineChartData[];
  last12Quarters: TQuoter[];
}

export function useIrrByGroup(
  filteredData: IMetricsDataModel[],
  transactionGroupExtractor: GroupExtractorFn
): IrrByGroup {
  const date = useAtomValue(selectedViewDatePFJ);
  const last12Quarters = useMemo(
    () =>
      QuartersBuilder.last12Quarters(date ?? new Date()).sort(
        (a: TQuoter, b: TQuoter) => a.start.getTime() - b.start.getTime()
      ),
    [date]
  );
  const getCompany = useCompanyMapLookup();

  const data = useMemo(() => {
    const datasetPerGroup: Record<string, TQuarterTransactions> = {};

    function getTransactionQuarters(
      transaction: MetricsTransactionDataModel,
      quarters: TQuoter[]
    ): TQuoter[] {
      if (!transaction.transactionDate) {
        return [];
      }

      const tDate = new Date(transaction.transactionDate);
      tDate.setHours(0);
      tDate.setMinutes(0);
      tDate.setSeconds(0);

      return quarters.filter((q) => tDate <= q.end);
    }

    function getFundQuarters(quarters: TQuoter[]) {
      return quarters.reduce((map, q) => ({ ...map, [q.label]: [] }), {});
    }

    filteredData?.forEach((rawMetric) => {
      rawMetric.transactions.forEach((transaction) => {
        const company = getCompany(rawMetric.companyId);
        if (!company) {
          return;
        }
        const transactionGroup = transactionGroupExtractor({ transaction, company });
        if (!transactionGroup) {
          return;
        }

        if (!datasetPerGroup[transactionGroup]) {
          datasetPerGroup[transactionGroup] = getFundQuarters(last12Quarters);
        }

        const transactionQuarters = getTransactionQuarters(transaction, last12Quarters);

        if (!transactionQuarters?.length) {
          return;
        }

        transactionQuarters.forEach((quarter) => {
          if (!datasetPerGroup[transactionGroup][quarter.label]) {
            datasetPerGroup[transactionGroup][quarter.label] = [];
          }

          datasetPerGroup[transactionGroup][quarter.label].push(transaction);
        });
      });
    });

    const fundQuarterXirr: Record<string, Record<string, number | null>> = {};
    Object.keys(datasetPerGroup).forEach((fundName) => {
      last12Quarters.forEach((quarter) => {
        const quarterTransactions = datasetPerGroup[fundName][quarter.label] ?? [];
        // A transaction's currentInvestment is its share of FMV; pooled it is the
        // group's terminal value for the quarter, pinned to the quarter end.
        const fmv = quarterTransactions.reduce(
          (sum, transaction) => sum + (transaction.currentInvestment ?? 0),
          0
        );

        fundQuarterXirr[quarter.label] = {
          ...(fundQuarterXirr[quarter.label] ?? {}),
          [fundName]: calculatePoolIrr(
            transactionsToCashflows(quarterTransactions),
            fmv,
            startOfDay(quarter.end)
          ),
        };
      });
    });

    const chartData = Object.keys(fundQuarterXirr).map((quarter) => ({
      ...(fundQuarterXirr[quarter] as Record<string, number>),
      period: quarter,
    }));
    return mapLineChartData(chartData);
  }, [filteredData, getCompany, last12Quarters, transactionGroupExtractor]);

  return useMemo(() => ({ data, last12Quarters }), [data, last12Quarters]);
}
